/**
 * GsplatRevealRadial - Radial reveal effect for gaussian splats
 *
 * Creates two waves emanating from a center point:
 * 1. Dot wave: Small colored dots appear progressively
 * 2. Lift wave: Particles lift up, get highlighted, then settle to original state
 *
 * Shader functions use PlayCanvas 2.16+ API:
 * - modifySplatCenter, modifySplatRotationScale, modifySplatColor
 * - gsplatGetSizeFromScale, gsplatMakeSpherical
 */
import * as pc from 'playcanvas';
import { relightingSnippetGLSL, relightingSnippetWGSL } from './gsplat-relighting';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';

const shaderGLSL = /* glsl */ `
uniform float uTime;
uniform vec3 uCenter;
uniform float uSpeed;
uniform float uAcceleration;
uniform float uDelay;
uniform vec3 uDotTint;
uniform vec3 uWaveTint;
uniform float uOscillationIntensity;
uniform float uEndRadius;
uniform float uBandWidth;
uniform float uWarpedRadius;  // warped max scene radius for reverse-wave

// Shared globals (initialized once per vertex)
float g_dist;
float g_dotWavePos;
float g_liftTime;
float g_liftWavePos;
float g_reverseReveal; // 0 = not reverse-revealed, 1 = fully revealed by reverse wave

void initShared(vec3 center) {
    float rawDist = length(center - uCenter);
    // Distance warp: compress far distances so distant splats reveal sooner.
    // See gsplat-reveal-bloom.ts for the derivation. K=5 matches bloom.
    const float K = 5.0;
    g_dist = rawDist / sqrt(1.0 + rawDist / K);
    g_dotWavePos = uSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;
    g_liftTime = max(0.0, uTime - uDelay);
    g_liftWavePos = uSpeed * g_liftTime + 0.5 * uAcceleration * g_liftTime * g_liftTime;

    // Reverse wave: outer 10% of splats reveal from outside-in simultaneously.
    // distFromEdge is small for edge splats (reveal first), grows inward.
    // revWavePos grows over time → reveals progressively inward.
    g_reverseReveal = 0.0;
    float outerThreshold = uWarpedRadius * 0.9;
    if (g_dist > outerThreshold && uWarpedRadius > 0.0) {
        float distFromEdge = uWarpedRadius - g_dist;
        float revWavePos = uSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;
        g_reverseReveal = 1.0 - smoothstep(revWavePos - uBandWidth, revWavePos + uBandWidth, distFromEdge);
    }
}

// Hash function for per-splat randomization
float hash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

void modifySplatCenter(inout vec3 center) {
    initShared(center);

    // Early exit optimization
    if (g_dist > uEndRadius) {
        rl_casterCenter(center);
        return;
    }

    // Only apply oscillation if lift wave hasn't fully passed
    bool wavesActive = g_liftTime <= 0.0 || g_dist > g_liftWavePos - 1.5 * uBandWidth;
    if (wavesActive) {
        // Apply oscillation with per-splat phase offset
        float phase = hash(center) * 6.28318;
        center.y += sin(uTime * 3.0 + phase) * uOscillationIntensity * 0.25;
    }

    // Apply lift effect near the wave edge
    float distToLiftWave = abs(g_dist - g_liftWavePos);
    if (distToLiftWave < 1.0 * uBandWidth && g_liftTime > 0.0) {
        float normalizedDist = distToLiftWave / uBandWidth;
        float liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
        center.y += liftAmount * uOscillationIntensity * 0.9;
    }
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);

    // Reverse wave: if fully revealed from outside-in, show at full size
    if (g_reverseReveal >= 1.0) return;

    // Store original scale for shape preservation
    vec3 origScale = scale;
    float origSize = gsplatGetSizeFromScale(scale);

    // Determine scale factor and phase
    float scaleFactor;
    bool isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_dist;

    if (isLiftWave) {
        // Lift wave: transition from dots to full size
        scaleFactor = (g_liftWavePos >= g_dist + 2.0) ? 1.0 : mix(0.1, 1.0, (g_liftWavePos - g_dist) * 0.5);
    } else if (g_dist > g_dotWavePos + 1.0) {
        // Before dot wave: use reverse reveal if active, otherwise invisible
        if (g_reverseReveal > 0.0) {
            scale *= g_reverseReveal;
        } else {
            scale = vec3(0.0);
        }
        return;
    } else if (g_dist > g_dotWavePos - 1.0) {
        // Dot wave front: scale from 0 to 0.1 with 2x peak at center
        float distToWave = abs(g_dist - g_dotWavePos);
        scaleFactor = (distToWave < 0.5)
            ? mix(0.1, 0.2, 1.0 - distToWave * 2.0)
            : mix(0.0, 0.1, smoothstep(g_dotWavePos + 1.0, g_dotWavePos - 1.0, g_dist));
    } else {
        // After dot wave, before lift: small dots
        scaleFactor = 0.1;
    }

    // Apply scale
    if (scaleFactor >= 1.0) {
        // Fully revealed: original shape and size (no-op)
        return;
    } else if (isLiftWave) {
        // Lift wave: lerp from spherical dots to original shape
        float t = (scaleFactor - 0.1) * 1.111111; // normalize [0.1, 1.0] to [0, 1]
        float dotSize = scaleFactor * 0.05;
        float finalSize = mix(dotSize, origSize, t);

        // Lerp between spherical (uniform) and scaled original
        vec3 sphericalScale = vec3(finalSize);
        vec3 scaledOrig = origScale * scaleFactor;
        scale = mix(sphericalScale, scaledOrig, t);
    } else {
        // Dot phase: spherical with absolute size, but don't make small splats larger
        float targetSize = min(scaleFactor * 0.05, origSize);
        gsplatMakeSpherical(scale, targetSize);
    }
}

void revealColorEffect(vec3 center, inout vec4 color) {
    // Use shared globals
    if (g_dist > uEndRadius) return;

    // Lift wave tint takes priority (active during lift)
    if (g_liftTime > 0.0 && g_dist >= g_liftWavePos - 1.5 * uBandWidth && g_dist <= g_liftWavePos + 0.5 * uBandWidth) {
        float distToLift = abs(g_dist - g_liftWavePos);
        float liftIntensity = smoothstep(1.5 * uBandWidth, 0.0, distToLift);
        color.rgb += uWaveTint * liftIntensity;
    }
    // Dot wave tint (active in dot phase, but not where lift wave is active)
    else if (g_dist <= g_dotWavePos && (g_liftTime <= 0.0 || g_dist > g_liftWavePos + 0.5 * uBandWidth)) {
        float distToDot = abs(g_dist - g_dotWavePos);
        float dotIntensity = smoothstep(1.0 * uBandWidth, 0.0, distToDot);
        color.rgb += uDotTint * dotIntensity;
    }
}

void modifySplatColor(vec3 center, inout vec4 color) {
    // EFFECT_HOOK
    revealColorEffect(center, color);
}
`;

const shaderWGSL = /* wgsl */ `
uniform uTime: f32;
uniform uCenter: vec3f;
uniform uSpeed: f32;
uniform uAcceleration: f32;
uniform uDelay: f32;
uniform uDotTint: vec3f;
uniform uWaveTint: vec3f;
uniform uOscillationIntensity: f32;
uniform uEndRadius: f32;
uniform uBandWidth: f32;
uniform uWarpedRadius: f32;

// Shared globals (initialized once per vertex)
var<private> g_dist: f32;
var<private> g_dotWavePos: f32;
var<private> g_liftTime: f32;
var<private> g_liftWavePos: f32;
var<private> g_reverseReveal: f32;

fn initShared(center: vec3f) {
    let rawDist = length(center - uniform.uCenter);
    // Distance warp: compress far distances so distant splats reveal sooner.
    // See gsplat-reveal-bloom.ts for the derivation. K=5 matches bloom.
    let K: f32 = 5.0;
    g_dist = rawDist / sqrt(1.0 + rawDist / K);
    g_dotWavePos = uniform.uSpeed * uniform.uTime + 0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;
    g_liftTime = max(0.0, uniform.uTime - uniform.uDelay);
    g_liftWavePos = uniform.uSpeed * g_liftTime + 0.5 * uniform.uAcceleration * g_liftTime * g_liftTime;

    // Reverse wave: outer 10% of splats reveal from outside-in simultaneously.
    // distFromEdge is small for edge splats (reveal first), grows inward.
    // revWavePos grows over time → reveals progressively inward.
    g_reverseReveal = 0.0;
    let outerThreshold = uniform.uWarpedRadius * 0.9;
    if (g_dist > outerThreshold && uniform.uWarpedRadius > 0.0) {
        let distFromEdge = uniform.uWarpedRadius - g_dist;
        let revWavePos = uniform.uSpeed * uniform.uTime + 0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;
        g_reverseReveal = 1.0 - smoothstep(revWavePos - uniform.uBandWidth, revWavePos + uniform.uBandWidth, distFromEdge);
    }
}

// Hash function for per-splat randomization
fn hash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
    initShared(*center);

    // Early exit optimization
    if (g_dist > uniform.uEndRadius) {
        rl_casterCenter(center);
        return;
    }

    // Only apply oscillation if lift wave hasn't fully passed
    let wavesActive = g_liftTime <= 0.0 || g_dist > g_liftWavePos - 1.5 * uniform.uBandWidth;
    if (wavesActive) {
        // Apply oscillation with per-splat phase offset
        let phase = hash(*center) * 6.28318;
        (*center).y += sin(uniform.uTime * 3.0 + phase) * uniform.uOscillationIntensity * 0.25;
    }

    // Apply lift effect near the wave edge
    let distToLiftWave = abs(g_dist - g_liftWavePos);
    if (distToLiftWave < 1.0 * uniform.uBandWidth && g_liftTime > 0.0) {
        let normalizedDist = distToLiftWave / uniform.uBandWidth;
        let liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
        (*center).y += liftAmount * uniform.uOscillationIntensity * 0.9;
    }
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);

    // Reverse wave: if fully revealed from outside-in, show at full size
    if (g_reverseReveal >= 1.0) {
        return;
    }

    // Store original scale for shape preservation
    let origScale = *scale;
    let origSize = gsplatGetSizeFromScale(*scale);

    // Determine scale factor and phase
    var scaleFactor: f32;
    let isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_dist;

    if (isLiftWave) {
        // Lift wave: transition from dots to full size
        scaleFactor = select(mix(0.1, 1.0, (g_liftWavePos - g_dist) * 0.5), 1.0, g_liftWavePos >= g_dist + 2.0);
    } else if (g_dist > g_dotWavePos + 1.0) {
        // Before dot wave: use reverse reveal if active, otherwise invisible
        if (g_reverseReveal > 0.0) {
            *scale *= g_reverseReveal;
        } else {
            *scale = vec3f(0.0);
        }
        return;
    } else if (g_dist > g_dotWavePos - 1.0) {
        // Dot wave front: scale from 0 to 0.1 with 2x peak at center
        let distToWave = abs(g_dist - g_dotWavePos);
        scaleFactor = select(
            mix(0.0, 0.1, smoothstep(g_dotWavePos + 1.0, g_dotWavePos - 1.0, g_dist)),
            mix(0.1, 0.2, 1.0 - distToWave * 2.0),
            distToWave < 0.5
        );
    } else {
        // After dot wave, before lift: small dots
        scaleFactor = 0.1;
    }

    // Apply scale
    if (scaleFactor >= 1.0) {
        // Fully revealed: original shape and size (no-op)
        return;
    } else if (isLiftWave) {
        // Lift wave: lerp from spherical dots to original shape
        let t = (scaleFactor - 0.1) * 1.111111; // normalize [0.1, 1.0] to [0, 1]
        let dotSize = scaleFactor * 0.05;
        let finalSize = mix(dotSize, origSize, t);

        // Lerp between spherical (uniform) and scaled original
        let sphericalScale = vec3f(finalSize);
        let scaledOrig = origScale * scaleFactor;
        *scale = mix(sphericalScale, scaledOrig, t);
    } else {
        // Dot phase: spherical with absolute size, but don't make small splats larger
        let targetSize = min(scaleFactor * 0.05, origSize);
        gsplatMakeSpherical(scale, targetSize);
    }
}

fn revealColorEffect(center: vec3f, color: ptr<function, vec4f>) {
    // Use shared globals
    if (g_dist > uniform.uEndRadius) {
        return;
    }

    // Lift wave tint takes priority (active during lift)
    if (g_liftTime > 0.0 && g_dist >= g_liftWavePos - 1.5 * uniform.uBandWidth && g_dist <= g_liftWavePos + 0.5 * uniform.uBandWidth) {
        let distToLift = abs(g_dist - g_liftWavePos);
        let liftIntensity = smoothstep(1.5 * uniform.uBandWidth, 0.0, distToLift);
        (*color) = vec4f((*color).rgb + uniform.uWaveTint * liftIntensity, (*color).a);
    }
    // Dot wave tint (active in dot phase, but not where lift wave is active)
    else if (g_dist <= g_dotWavePos && (g_liftTime <= 0.0 || g_dist > g_liftWavePos + 0.5 * uniform.uBandWidth)) {
        let distToDot = abs(g_dist - g_dotWavePos);
        let dotIntensity = smoothstep(1.0 * uniform.uBandWidth, 0.0, distToDot);
        (*color) = vec4f((*color).rgb + uniform.uDotTint * dotIntensity, (*color).a);
    }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    // EFFECT_HOOK
    revealColorEffect(center, color);
}
`;

// Cache for the script class
let _GsplatRevealRadialClass: typeof pc.ScriptType | null = null;

type ScriptTypeWithPrototype<T extends pc.ScriptType> = typeof pc.ScriptType & {
    prototype: T;
};

interface GsplatRevealRadialScript extends pc.ScriptType {
    effectTime: number;
    material: pc.Material | null;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    _centerArray: [number, number, number];
    _dotTintArray: [number, number, number];
    _waveTintArray: [number, number, number];
    center: pc.Vec3 | null;
    speed: number;
    acceleration: number;
    delay: number;
    dotTint: pc.Color | null;
    waveTint: pc.Color | null;
    oscillationIntensity: number;
    endRadius: number;
    _autoEndRadius: number;
    bandWidth: number;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
    _applyShaders: () => void;
    _applyShaderToMaterial: (material: pc.Material) => void;
    _removeShaders: () => void;
    _updateUniforms: () => void;
    _setUniform: (name: string, value: number | number[]) => void;
    _detectSceneRadius: () => void;
    _getCompletionTime: () => number;
    _isEffectComplete: () => boolean;
}

/**
 * Gets or creates the GsplatRevealRadial script class.
 * Must be called after PlayCanvas app is initialized.
 */
export function getGsplatRevealRadialClass(): typeof pc.ScriptType {
    if (_GsplatRevealRadialClass) {
        return _GsplatRevealRadialClass;
    }

    const GsplatRevealRadial = pc.createScript('gsplatRevealRadial') as ScriptTypeWithPrototype<GsplatRevealRadialScript>;

    Object.assign(GsplatRevealRadial.prototype, {
        effectTime: 0,
        material: null as pc.Material | null,
        _effectInitialized: false,
        _shadersNeedApplication: false,
        _centerArray: [0, 0, 0],
        _dotTintArray: [0, 0, 0],
        _waveTintArray: [0, 0, 0],

        // Effect properties
        center: null as pc.Vec3 | null,
        speed: 20,
        acceleration: 0,
        delay: 0.5,
        dotTint: null as pc.Color | null,
        waveTint: null as pc.Color | null,
        oscillationIntensity: 0.2,
        endRadius: 500,
        _autoEndRadius: 0,
        bandWidth: 1.0,

        initialize(this: GsplatRevealRadialScript) {
            this.effectTime = 0;
            this.material = null;
            this._effectInitialized = false;
            this._shadersNeedApplication = false;
            this._centerArray = [0, 0, 0];
            this._dotTintArray = [0, 0, 0];
            this._waveTintArray = [0, 0, 0];

            if (!this.center) this.center = new pc.Vec3(0, 0, 0);
            if (!this.dotTint) this.dotTint = new pc.Color(0, 1, 1);
            if (!this.waveTint) this.waveTint = new pc.Color(1, 0.5, 0);

            this.on('enable', () => {
                this.effectTime = 0;
                if (this._effectInitialized) {
                    this._applyShaders();
                } else {
                    this._shadersNeedApplication = true;
                }
            });

            this.on('disable', () => {
                this._removeShaders();
            });

            if (!this.entity.gsplat) {
                return;
            }

            this._effectInitialized = true;
            this._detectSceneRadius();
            if (this.enabled) {
                this._applyShaders();
            }
        },

        update(this: GsplatRevealRadialScript, dt: number) {
            // Try to complete initialization
            if (!this._effectInitialized) {
                if (this.entity.gsplat) {
                    this._effectInitialized = true;
                    this._detectSceneRadius();
                    if (this.enabled && this._shadersNeedApplication) {
                        this._applyShaders();
                        this._shadersNeedApplication = false;
                    }
                }
                return;
            }

            if (this._shadersNeedApplication) {
                this._applyShaders();
                if (this.material) {
                    this._shadersNeedApplication = false;
                }
            }

            if (!this.material) return;

            this.effectTime += dt;

            if (this._isEffectComplete()) {
                console.log('[RevealEffect] Effect complete, disabling');
                this.enabled = false;
                return;
            }

            this._updateUniforms();
            this.material.update();
        },

        _updateUniforms(this: GsplatRevealRadialScript) {
            this._setUniform('uTime', this.effectTime);
            this._centerArray[0] = this.center!.x;
            this._centerArray[1] = this.center!.y;
            this._centerArray[2] = this.center!.z;
            this._setUniform('uCenter', this._centerArray);
            this._setUniform('uSpeed', this.speed);
            this._setUniform('uAcceleration', this.acceleration);
            this._setUniform('uDelay', this.delay);
            this._dotTintArray[0] = this.dotTint!.r;
            this._dotTintArray[1] = this.dotTint!.g;
            this._dotTintArray[2] = this.dotTint!.b;
            this._setUniform('uDotTint', this._dotTintArray);
            this._waveTintArray[0] = this.waveTint!.r;
            this._waveTintArray[1] = this.waveTint!.g;
            this._waveTintArray[2] = this.waveTint!.b;
            this._setUniform('uWaveTint', this._waveTintArray);
            this._setUniform('uOscillationIntensity', this.oscillationIntensity);
            const effectiveRadius = this._autoEndRadius > 0 ? this._autoEndRadius : this.endRadius;
            this._setUniform('uEndRadius', effectiveRadius);
            this._setUniform('uBandWidth', this.bandWidth);
            // Warped radius for the reverse wave (outer 10% reveals from outside-in)
            const K = 5.0;
            this._setUniform('uWarpedRadius', effectiveRadius / Math.sqrt(1.0 + effectiveRadius / K));
        },

        _detectSceneRadius(this: GsplatRevealRadialScript) {
            try {
                const gsplat = this.entity.gsplat;
                const inst = gsplat && (gsplat as any).instance;
                const mi = inst?.meshInstance ?? inst?.meshInstances?.[0];
                const aabb = mi?.aabb;
                if (aabb) {
                    const he = aabb.halfExtents;
                    const r = Math.sqrt(he.x * he.x + he.y * he.y + he.z * he.z);
                    this._autoEndRadius = r * 1.2;
                }
            } catch (_e) {
                // AABB not available; will use endRadius fallback
            }
        },

        _getCompletionTime(this: GsplatRevealRadialScript): number {
            const radius = this._autoEndRadius > 0 ? this._autoEndRadius : this.endRadius;
            // Apply the same distance warp as initShared() in the shader so auto-disable
            // fires when the warped wave actually reaches the farthest splat.
            // Must match shaderGLSL/WGSL: warped = raw / sqrt(1 + raw/K), K=5.
            const K = 5.0;
            const warpedRadius = radius / Math.sqrt(1.0 + radius / K);
            const liftStartTime = this.delay;
            if (this.acceleration === 0) {
                return liftStartTime + (warpedRadius / this.speed);
            }
            const discriminant = this.speed * this.speed + 2 * this.acceleration * warpedRadius;
            if (discriminant < 0) return Infinity;
            const t = (-this.speed + Math.sqrt(discriminant)) / this.acceleration;
            return liftStartTime + t;
        },

        _isEffectComplete(this: GsplatRevealRadialScript): boolean {
            return this.effectTime >= this._getCompletionTime();
        },

        getShaderGLSL(): string {
            return shaderGLSL;
        },

        getShaderWGSL(): string {
            return shaderWGSL;
        },

        _applyShaders(this: GsplatRevealRadialScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                // Unified mode: template material
                this.material = (this.app.scene as any).gsplat?.material ?? null;
                if (!this.material) {
                    console.warn('[RevealEffect] Unified gsplat template material not available');
                    return;
                }
                this._applyShaderToMaterial(this.material);
            } else {
                // Non-unified mode: component's own material
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                    this._applyShaderToMaterial(mat);
                } else {
                    // Material not ready yet, listen for load
                    gsplat.once('load', () => {
                        this.material = gsplat.material ?? null;
                        if (this.material) {
                            this._applyShaderToMaterial(this.material);
                        }
                    });
                }
            }
        },

        _applyShaderToMaterial(this: GsplatRevealRadialScript, material: pc.Material) {
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            let shader = lang === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL();

            // Check for sibling relighting script - if present, combine shaders
            // so relighting applies first, then reveal tinting on top
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                // Tell relighting script not to manage the shader chunk itself
                // Must be set BEFORE the combined shader is applied to avoid race with relighting's on('enable')
                relightScript._shaderManagedExternally = true;

                // relightingSnippet already includes mirrorClipSnippet, so no separate prepend needed
                const snippet = lang === 'wgsl' ? relightingSnippetWGSL : relightingSnippetGLSL;
                // Replace the EFFECT_HOOK sentinel with relighting call before the effect call
                shader = snippet + '\n' + shader.replace(
                    '// EFFECT_HOOK',
                    'applyRelighting(center, color);'
                );
            } else {
                // Standalone: prepend mirror clip snippet so mirrorClipSplat() is defined
                const mirrorSnippet = lang === 'wgsl' ? mirrorClipSnippetWGSL : mirrorClipSnippetGLSL;
                shader = mirrorSnippet + shader;
            }

            material.getShaderChunks(lang).set('gsplatModifyVS', shader);
            material.update();
        },

        _removeShaders(this: GsplatRevealRadialScript) {
            if (!this.material) return;

            // If relighting is active, hand material back and restore standalone relighting shader
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                relightScript.material = this.material;
                const device = this.app.graphicsDevice;
                const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
                const relightShader = lang === 'wgsl' ? relightScript.getShaderWGSL() : relightScript.getShaderGLSL();
                this.material.getShaderChunks(lang).set('gsplatModifyVS', relightShader);
                this.material.update();
                // Release external management AFTER shader is applied to avoid race
                relightScript._shaderManagedExternally = false;
                this.material = null;
                console.log('[RevealEffect] Handed material back to relighting');
                return;
            }

            // Check for compare clip script — if active, restore its shader instead of deleting
            const compareClipScript = this.entity.script && (this.entity.script as any).gsplatCompareClip;
            if (compareClipScript && compareClipScript.enabled) {
                compareClipScript.material = null;
                compareClipScript._shadersNeedApplication = true;
                this.material = null;
                return;
            }

            // No relighting or compare clip — clean up the shader chunk entirely
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';

            this.material.getShaderChunks(lang).delete('gsplatModifyVS');
            this.material.update();
            this.material = null;
        },

        _setUniform(this: GsplatRevealRadialScript, name: string, value: number | number[]) {
            this.material?.setParameter(name, value);
        },

        destroy(this: GsplatRevealRadialScript) {
            this._removeShaders();
        }
    });

    _GsplatRevealRadialClass = GsplatRevealRadial;
    return GsplatRevealRadial;
}

// For backwards compatibility
export const GsplatRevealRadial = {
    get class() {
        return getGsplatRevealRadialClass();
    }
};
